import { SpaceVisibilityField } from './SpaceVisibilityField';
import type { SpaceVisibilityValue } from './SpaceVisibilityField';
import { WorkspacesField } from '@/shared/ui/WorkspacesField';
import { useCurrentWorkspaceName } from '@/shared/hooks';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import {
  useCurrentOrganizationName,
  useCurrentOrganizationWorkspaces,
  usePermissions,
} from '@/entities/user';
import { FieldError } from '@monksflow/monks-ui';
import { EyeIcon } from 'lucide-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormSection } from './FormSection';
import { useTranslation } from 'react-i18next';

interface SpaceVisibilityFormData {
  spaceVisibility: SpaceVisibilityValue | null;
  workspaceIds: string[];
}

interface SpaceVisibilitySectionProps {
  disabled?: boolean;
  /** See `useVisibilityOptions`; the edit form drops the create-time "this workspace" shorthand. */
  includeCurrentWorkspace?: boolean;
}

/**
 * Shared space visibility form section.
 * Works with any react-hook-form that has a spaceVisibility field.
 * Validation is handled by the Zod schema in the form hook.
 *
 * Reads workspace/organization context here (Springboard dependency injection — lift the
 * cross-app `@/app/hooks/use-workspace-auth` read out of the spaceVisibility
 * feature so that feature stays boundary-clean) and forwards the data to
 * `<SpaceVisibilityField />` as props.
 */
export function SpaceVisibilitySection(props: SpaceVisibilitySectionProps) {
  const { t } = useTranslation('audiences');

  const { disabled, includeCurrentWorkspace } = props;
  const { control, getValues, setValue } = useFormContext<SpaceVisibilityFormData>();
  const organizationName = useCurrentOrganizationName();
  const workspaceName = useCurrentWorkspaceName();
  const { isOrganizationAdmin } = usePermissions();
  const canSelectWorkspaces = useBooleanFlagValue('persona_workspace_selection', false);

  const workspaces = useCurrentOrganizationWorkspaces();
  const currentWorkspaceId = useCurrentWorkspaceId();
  // useWatch rather than the context's `watch`: the latter registers on the shared control, so
  // this read would re-render whichever component owns the form, not just this section.
  const spaceVisibility = useWatch({ control, name: 'spaceVisibility' });
  const showWorkspacesField = canSelectWorkspaces && spaceVisibility === 'workspaces';

  // An org-wide persona in the hands of somebody who is not an organization admin: every move
  // away from it needs that role, and the API refuses the save. The choices are shown, with the
  // current one selected, so the state is legible — but none of them is on offer.
  const visibilityIsReadOnly = !isOrganizationAdmin && spaceVisibility === 'organization';

  // The picker shows the caller's own workspace as a chip that carries no remove control, because
  // the API grants it whatever the request said. Writing it into form state as the choice is made
  // is what keeps that display honest: otherwise the field reads "1 of 3 selected" while the
  // schema still sees an empty list, and sharing with only your own workspace is unreachable.
  const seedOwnWorkspace = () => {
    if (!currentWorkspaceId) return;
    const workspaceIds = getValues('workspaceIds');
    if (!workspaceIds.includes(currentWorkspaceId)) {
      setValue('workspaceIds', [currentWorkspaceId, ...workspaceIds]);
    }
  };

  return (
    <FormSection
      icon={<EyeIcon />}
      title={t('audiences.spaceVisibilitySection.spaceVisibility')}
      description={t('audiences.spaceVisibilitySection.setTheSpaceVisibilityFor')}
    >
      <Controller
        name="spaceVisibility"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <>
            <SpaceVisibilityField
              value={field.value}
              onChange={(next) => {
                field.onChange(next);
                if (next === 'workspaces') seedOwnWorkspace();
              }}
              isOrganizationAdmin={isOrganizationAdmin}
              organizationName={organizationName}
              workspaceName={workspaceName}
              canSelectWorkspaces={canSelectWorkspaces}
              includeCurrentWorkspace={includeCurrentWorkspace}
              disabled={disabled || visibilityIsReadOnly}
            />
            {error && <FieldError className="mt-2">{error.message}</FieldError>}
          </>
        )}
      />

      {showWorkspacesField && (
        <Controller
          name="workspaceIds"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <WorkspacesField
              options={workspaces}
              lockedWorkspaceId={currentWorkspaceId ?? undefined}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
              error={error?.message}
              helperText={t('audiences.workspacesField.onlyUsersInSelectedWorkspaces')}
            />
          )}
        />
      )}
    </FormSection>
  );
}
